<template>
  <div class="top-header-wrap">
  <header class="top-header" :class="{ 'top-header--guest': !isLoggedIn }">
    <a href="#" class="logo" @click.prevent="$emit('home')">
      <img v-if="logoUrl" :src="logoUrl" :alt="brandName" class="logo-img">
      <span v-else class="logo-crown">♛</span>
    </a>

    <div class="top-header__center">
      <nav class="main-nav">
        <a
          v-for="(item, idx) in topNavItems"
          :key="item.key"
          href="#"
          :class="{ active: activeNav === idx }"
          @click.prevent="$emit('nav', idx)"
        >{{ item.label }}</a>
      </nav>

      <button
        v-if="isLoggedIn && slipUnreadCount > 0"
        type="button"
        class="header-slip-notice"
        :aria-label="slipNoticeAria"
        @click="$emit('open-slip')"
      >
        <span class="header-slip-notice__dot" aria-hidden="true" />
        <span class="header-slip-notice__text">{{ slipNoticeText }}</span>
      </button>

      <div
        v-if="showOnlineUsers"
        class="header-online-users header-online-users--desktop"
        :aria-label="onlineUsersAria"
      >
        <span class="header-online-users__dot" aria-hidden="true" />
        <span class="header-online-users__label">{{ $t('nav.onlineUsers') }}</span>
        <strong class="header-online-users__count">{{ onlineUsersDisplay }}</strong>
      </div>
    </div>

    <div class="header-buttons">
      <div
        v-if="showOnlineUsers"
        class="header-online-users header-online-users--mobile"
        :aria-label="onlineUsersAria"
      >
        <span class="header-online-users__dot" aria-hidden="true" />
        <span class="header-online-users__label">{{ $t('nav.onlineUsers') }}</span>
        <strong class="header-online-users__count">{{ onlineUsersDisplay }}</strong>
      </div>
      <div
        v-if="isLoggedIn"
        ref="headerUser"
        class="header-user"
        :class="{ 'header-user--stacked': userInfoStacked }"
      >
        <strong class="header-user__id">{{ loginId }}</strong>
        <span class="header-user__sep header-user__sep--identity" aria-hidden="true" />
        <div class="header-user__wallet">
          <div class="header-user__money">
            <strong class="header-user__balance">{{ displayBalance }}</strong>
            <button
              type="button"
              class="header-user__refresh"
              :class="{ 'header-user__refresh--loading': balanceLoading }"
              :aria-label="$t('common.balanceRefresh')"
              :disabled="balanceLoading"
              @click="$emit('refresh-balance')"
            >
              <svg
                class="header-user__refresh-icon"
                viewBox="0 0 24 24"
                width="16"
                height="16"
                aria-hidden="true"
              >
                <path
                  d="M20 12a8 8 0 1 1-2.34-5.66M20 4v5h-5M4 12a8 8 0 1 1 2.34 5.66M4 20v-5h5"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.75"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>
          <span class="header-user__sep header-user__sep--point" aria-hidden="true" />
          <div class="header-user__point">
            <span class="header-user__point-label">{{ $t('nav.headerPoint') }}</span>
            <strong class="header-user__point-value">{{ displayPoint }}</strong>
            <button
              type="button"
              class="header-user__point-convert"
              @click="$emit('point-convert')"
            >{{ $t('nav.headerPointConvert') }}</button>
          </div>
        </div>
      </div>
      <button
        v-if="isLoggedIn"
        type="button"
        class="btn-logout header-user__logout"
        @click="$emit('logout')"
      >{{ $t('nav.logout') }}</button>

      <template v-else>
        <button type="button" class="btn-login" @click="$emit('login')">{{ $t('nav.login') }}</button>
        <button type="button" class="btn-join" @click="$emit('signup')">{{ $t('nav.signup') }}</button>
      </template>

      <button
        type="button"
        class="btn-lang"
        :aria-label="$t('lang.title')"
        @click="$emit('lang')"
      >
        <svg class="btn-lang__icon" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
          <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.75" />
          <path
            d="M3 12h18M12 3c2.5 2.8 4 6 4 9s-1.5 6.2-4 9M12 3c-2.5 2.8-4 6-4 9s1.5 6.2 4 9"
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
            stroke-linecap="round"
          />
        </svg>
        <span class="btn-lang__code">{{ localeShort }}</span>
      </button>
      <button
        type="button"
        class="btn-menu"
        :class="{ 'btn-menu--open': menuOpen }"
        :aria-label="menuOpen ? $t('nav.menuClose') : $t('nav.menuOpen')"
        @click="$emit('toggle-menu')"
      >{{ menuOpen ? '✕' : '☰' }}</button>
    </div>
  </header>

  <button
    v-if="isLoggedIn && slipUnreadCount > 0"
    type="button"
    class="header-slip-banner"
    :aria-label="slipNoticeAria"
    @click="$emit('open-slip')"
  >
    <span class="header-slip-banner__dot" aria-hidden="true" />
    <span class="header-slip-banner__text">{{ slipNoticeText }}</span>
  </button>
  </div>
</template>

<script>
import { getLocaleMeta } from '@/utils/locale'
import { formatPointAmount } from '@/utils/format'
import { resolveSiteLogoUrl } from '@/utils/siteBrand'
import { formatOnlineUserCount, resolveOnlineUserCount } from '@/utils/siteOnlineUsers'

export default {
  name: 'BlackGoldHeader',
  props: {
    activeNav: { type: Number, default: 0 },
    isLoggedIn: { type: Boolean, default: false },
    loginId: { type: String, default: '' },
    balance: { type: Number, default: null },
    point: { type: Number, default: null },
    balanceLoading: { type: Boolean, default: false },
    menuOpen: { type: Boolean, default: false },
    slipUnreadCount: { type: Number, default: 0 },
    topNavItems: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    logoUrl () {
      return resolveSiteLogoUrl(this.$store.state.siteInfo)
    },
    brandName () {
      return this.$t('brand.name')
    },
    displayBalance () {
      if (this.balanceLoading && (this.balance === null || this.balance === undefined)) {
        return this.$t('common.loading')
      }
      return this.formatBalance(this.balance)
    },
    displayPoint () {
      if (this.balanceLoading && (this.point === null || this.point === undefined)) {
        return this.$t('common.loading')
      }
      return `${formatPointAmount(this.point)} P`
    },
    slipNoticeText () {
      return this.$t('slip.headerNotice')
    },
    slipNoticeAria () {
      return this.$t('slip.headerNoticeAria')
    },
    localeShort () {
      const locale = this.$i18n.locale
      return getLocaleMeta(locale).short || String(locale || 'ko').toUpperCase()
    },
    onlineUserCount () {
      return resolveOnlineUserCount(this.$store.state.siteInfo)
    },
    showOnlineUsers () {
      return this.onlineUserCount != null
    },
    onlineUsersDisplay () {
      return formatOnlineUserCount(this.onlineUserCount, this.$i18n.locale)
    },
    onlineUsersAria () {
      return this.$t('nav.onlineUsersAria', { count: this.onlineUsersDisplay })
    }
  },
  data () {
    return {
      userInfoStacked: false,
      headerUserStackObserver: null,
      headerUserStackFrame: null
    }
  },
  watch: {
    isLoggedIn (loggedIn) {
      if (loggedIn) {
        this.$nextTick(() => this.initHeaderUserStackObserver())
      } else {
        this.destroyHeaderUserStackObserver()
        this.userInfoStacked = false
      }
    },
    loginId () {
      this.scheduleHeaderUserStackCheck()
    },
    balance () {
      this.scheduleHeaderUserStackCheck()
    },
    balanceLoading () {
      this.scheduleHeaderUserStackCheck()
    },
    '$i18n.locale' () {
      this.scheduleHeaderUserStackCheck()
    }
  },
  mounted () {
    this.$nextTick(() => this.initHeaderUserStackObserver())
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.scheduleHeaderUserStackCheck, { passive: true })
    }
  },
  beforeDestroy () {
    this.destroyHeaderUserStackObserver()
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.scheduleHeaderUserStackCheck)
    }
  },
  methods: {
    scheduleHeaderUserStackCheck () {
      if (this.headerUserStackFrame) {
        window.cancelAnimationFrame(this.headerUserStackFrame)
      }
      this.headerUserStackFrame = window.requestAnimationFrame(() => {
        this.headerUserStackFrame = null
        this.checkHeaderUserStack()
      })
    },
    initHeaderUserStackObserver () {
      if (!this.isLoggedIn || typeof window === 'undefined') return

      this.destroyHeaderUserStackObserver()
      const el = this.$refs.headerUser
      if (!el) return

      if (window.ResizeObserver) {
        this.headerUserStackObserver = new ResizeObserver(() => {
          this.checkHeaderUserStack()
        })
        this.headerUserStackObserver.observe(el)
        if (el.parentElement) {
          this.headerUserStackObserver.observe(el.parentElement)
        }
      }

      this.checkHeaderUserStack()
    },
    destroyHeaderUserStackObserver () {
      if (this.headerUserStackObserver) {
        this.headerUserStackObserver.disconnect()
        this.headerUserStackObserver = null
      }
      if (this.headerUserStackFrame) {
        window.cancelAnimationFrame(this.headerUserStackFrame)
        this.headerUserStackFrame = null
      }
    },
    checkHeaderUserStack () {
      if (!this.isLoggedIn) {
        this.userInfoStacked = false
        return
      }

      if (typeof window !== 'undefined' && window.matchMedia('(min-width: 901px)').matches) {
        this.userInfoStacked = false
        return
      }

      const root = this.$refs.headerUser
      if (!root) return

      const idEl = root.querySelector('.header-user__id')
      const moneyEl = root.querySelector('.header-user__money')
      if (!idEl || !moneyEl) {
        this.userInfoStacked = false
        return
      }

      const gap = 8
      const availableWidth = this.getHeaderUserAvailableWidth(root)
      this.userInfoStacked = false

      this.$nextTick(() => {
        const idWidth = idEl.offsetWidth
        const moneyWidth = moneyEl.offsetWidth
        this.userInfoStacked = (idWidth + moneyWidth + gap) > availableWidth
      })
    },
    getHeaderUserAvailableWidth (root) {
      const buttons = root && root.parentElement
      if (!buttons) return root.clientWidth

      const gap = 8
      let siblingsWidth = 0
      Array.from(buttons.children).forEach(el => {
        if (el !== root) {
          siblingsWidth += el.offsetWidth + gap
        }
      })

      return Math.max(0, buttons.clientWidth - siblingsWidth)
    }
  }
}
</script>
